import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api, API } from "@/lib/api";
import {
  Trophy, RefreshCw, Crown, Clock, CheckCircle, XCircle,
  AlertCircle, Star, X, ChevronRight, Calendar, Briefcase,
  Play, TrendingUp, Target, Zap,
} from "lucide-react";

const POLL_MS = 15000;

const VERDICT_CFG = {
  selected:    { bg: "rgba(74,222,128,0.12)",  color: "#4ade80",   icon: CheckCircle, label: "Selected"    },
  shortlisted: { bg: "rgba(74,222,128,0.12)",  color: "#4ade80",   icon: CheckCircle, label: "Shortlisted" },
  conditional: { bg: "rgba(250,204,21,0.10)",  color: "#facc15",   icon: Star,        label: "Conditional" },
  rejected:    { bg: "rgba(239,68,68,0.10)",   color: "#ef4444",   icon: XCircle,     label: "Rejected"    },
  pending:     { bg: "rgba(255,255,255,0.05)", color: "#ffffff60", icon: AlertCircle, label: "Pending"     },
};

function VerdictBadge({ verdict }) {
  const cfg = VERDICT_CFG[verdict] || VERDICT_CFG.pending;
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono-kl text-[9px] uppercase tracking-widest"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function ScoreBar({ score, max, compact }) {
  const pct   = max > 0 ? Math.min(100, (score / max) * 100) : 0;
  const color = pct >= 70 ? "#4ade80" : pct >= 50 ? "#facc15" : "#e63946";
  return (
    <div className="flex items-center gap-3">
      <div className={`flex-1 rounded-full ${compact ? "h-1" : "h-1.5"}`} style={{ background: "rgba(255,255,255,0.08)" }}>
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color, boxShadow: `0 0 8px ${color}60` }} />
      </div>
      <span className={`font-display ${compact ? "text-xs" : "text-sm"}`} style={{ color, minWidth: compact ? 32 : 40 }}>
        {score}<span className="text-white/25 font-mono-kl text-[9px]">/{max}</span>
      </span>
    </div>
  );
}

function KpiBar({ label, score, max, notes }) {
  const pct   = max > 0 ? Math.round((score / max) * 100) : 0;
  const color = pct >= 70 ? "#4ade80" : pct >= 50 ? "#facc15" : "#e63946";
  return (
    <div className="mb-3">
      <div className="flex justify-between mb-1">
        <span className="font-mono-kl text-[10px] text-white/65 capitalize">{label.replace(/_/g, " ")}</span>
        <span className="font-mono-kl text-[10px] font-bold" style={{ color }}>{score}/{max}</span>
      </div>
      <div className="h-1 rounded-full" style={{ background: "rgba(255,255,255,0.07)" }}>
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color, boxShadow: `0 0 5px ${color}66` }} />
      </div>
      {notes && <p className="font-mono-kl text-[9px] text-white/30 mt-1 leading-relaxed">{notes}</p>}
    </div>
  );
}

function ScoreDonut({ score, max }) {
  const pct   = max > 0 ? score / max : 0;
  const r     = 40;
  const circ  = 2 * Math.PI * r;
  const dash  = pct * circ;
  const color = pct >= 0.7 ? "#4ade80" : pct >= 0.55 ? "#facc15" : "#e63946";
  return (
    <svg width={96} height={96} className="flex-shrink-0">
      <circle cx={48} cy={48} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={8} />
      <circle cx={48} cy={48} r={r} fill="none" stroke={color} strokeWidth={8}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform="rotate(-90 48 48)"
        style={{ transition: "stroke-dasharray 1.2s ease-out", filter: `drop-shadow(0 0 6px ${color})` }} />
      <text x={48} y={44} textAnchor="middle" fill="#f1f1f1" fontSize={18} fontWeight={900} fontFamily="monospace">{score}</text>
      <text x={48} y={60} textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize={10}>of {max}</text>
    </svg>
  );
}

function RankBadge({ rank }) {
  if (rank === 1) return <span className="text-2xl">🥇</span>;
  if (rank === 2) return <span className="text-2xl">🥈</span>;
  if (rank === 3) return <span className="text-2xl">🥉</span>;
  return <span className="font-display text-base text-white/30">#{rank}</span>;
}

function fmtDuration(seconds) {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s < 10 ? "0" : ""}${s}s`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
      year: "numeric", hour: "2-digit", minute: "2-digit",
    }) + " IST";
  } catch { return iso; }
}

function bestVerdict(sessions) {
  const order = { selected: 0, shortlisted: 0, conditional: 1, rejected: 2, pending: 3 };
  const sorted = [...sessions].sort((a, b) => (order[a.verdict] ?? 9) - (order[b.verdict] ?? 9));
  return sorted[0]?.verdict ? (VERDICT_CFG[sorted[0].verdict]?.label || "—") : "—";
}

/* ── Recording modal ── */
function RecordingModal({ sessionId, onClose }) {
  const token = localStorage.getItem("proctorly_admin_token") || localStorage.getItem("mock_token") || "";
  const src   = `${API}/mock/session/${sessionId}/recording-stream?token=${encodeURIComponent(token)}`;
  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div className="relative w-full max-w-4xl pointer-events-auto rounded-2xl overflow-hidden"
          style={{ background: "#000", boxShadow: "0 0 80px rgba(0,0,0,0.9)" }}>
          <button onClick={onClose}
            className="absolute top-3 right-3 z-10 text-white/60 hover:text-white bg-black/50 rounded-full p-1.5 transition-colors">
            <X className="w-4 h-4" />
          </button>
          <video src={src} controls autoPlay className="w-full max-h-[75vh]"
            style={{ display: "block" }}
            onError={(e) => { e.target.outerHTML = `<div style="padding:40px;text-align:center;color:rgba(255,255,255,0.4);font-family:monospace;font-size:12px">Recording not available for this session.</div>`; }}
          />
        </div>
      </div>
    </>
  );
}

/* ── Candidate detail drawer ── */
function CandidateDrawer({ entry, rank, onClose }) {
  const [activeSession, setActiveSession] = useState(0);
  const [recording,     setRecording]     = useState(null); // session_id to play

  const accentClr = { 1: "#e63946", 2: "#60a5fa", 3: "#fb923c" };
  const accent    = rank <= 3 ? accentClr[rank] : "#e63946";
  const sessions  = entry.sessions || [];
  const bestScore = Math.max(...sessions.map((s) => s.total_score), 0);
  const cur       = sessions[activeSession] || sessions[0];

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-2xl overflow-y-auto"
        style={{ background: "linear-gradient(160deg, #0d1117 0%, #0a0d13 100%)", borderLeft: `1px solid ${accent}30`, boxShadow: `-20px 0 60px rgba(0,0,0,0.6)` }}>

        {/* Sticky header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/8"
          style={{ background: "rgba(10,13,19,0.95)", backdropFilter: "blur(12px)" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center font-display text-lg flex-shrink-0"
              style={{ background: `${accent}18`, border: `1.5px solid ${accent}40`, color: accent }}>
              {(entry.name || "?")[0].toUpperCase()}
            </div>
            <div>
              <div className="font-display text-base uppercase text-white leading-tight">{entry.name}</div>
              <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/35">
                {sessions.length} session{sessions.length !== 1 ? "s" : ""} · <RankBadge rank={rank} />
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/5">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-6">
          {/* Stats strip */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: "Best Score",   value: `${bestScore}/${entry.total_max || 20}`, Icon: TrendingUp },
              { label: "Sessions",     value: sessions.length,                          Icon: Briefcase  },
              { label: "Best Result",  value: bestVerdict(sessions),                    Icon: Target     },
            ].map((stat, i) => (
              <div key={i} className="rounded-xl p-3 text-center"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <stat.Icon className="w-4 h-4 mx-auto mb-1" style={{ color: accent }} />
                <div className="font-display text-lg text-white leading-tight">{stat.value}</div>
                <div className="font-mono-kl text-[8px] uppercase tracking-widest text-white/35 mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Session tabs */}
          {sessions.length > 1 && (
            <div className="flex gap-2 flex-wrap mb-5">
              {sessions.map((s, i) => (
                <button key={i} onClick={() => setActiveSession(i)}
                  className="font-mono-kl text-[9px] uppercase tracking-widest px-3 py-1.5 rounded-full transition-all"
                  style={{
                    background: activeSession === i ? `${accent}20` : "rgba(255,255,255,0.04)",
                    color:      activeSession === i ? accent : "rgba(255,255,255,0.4)",
                    border:     activeSession === i ? `1px solid ${accent}50` : "1px solid rgba(255,255,255,0.07)",
                  }}>
                  Attempt {i + 1}
                  {s.total_score === bestScore && <span className="ml-1 opacity-70">★</span>}
                </button>
              ))}
            </div>
          )}

          {cur && (
            <div className="space-y-4">
              {/* Verdict + donut */}
              <div className="rounded-xl p-4 flex items-center gap-4"
                style={{
                  background: (VERDICT_CFG[cur.verdict] || VERDICT_CFG.pending).bg,
                  border: `1px solid ${(VERDICT_CFG[cur.verdict] || VERDICT_CFG.pending).color}30`,
                }}>
                <ScoreDonut score={cur.total_score} max={cur.total_max || 20} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <VerdictBadge verdict={cur.verdict} />
                    <span className="font-mono-kl text-[9px] text-white/35">{cur.position}</span>
                  </div>
                  {cur.feedback_text && (
                    <p className="text-white/60 text-xs leading-relaxed">{cur.feedback_text}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 flex-wrap">
                    <span className="font-mono-kl text-[9px] text-white/35 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />{fmtDate(cur.started_at)}
                    </span>
                    <span className="font-mono-kl text-[9px] text-white/35 flex items-center gap-1">
                      <Clock className="w-3 h-3" />{fmtDuration(cur.duration_seconds)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Watch recording button */}
              {cur.has_recording && cur.session_id && (
                <button onClick={() => setRecording(cur.session_id)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-mono-kl text-[10px] uppercase tracking-widest transition-all hover:opacity-90"
                  style={{ background: `${accent}18`, border: `1px solid ${accent}40`, color: accent }}>
                  <Play className="w-3.5 h-3.5" />
                  Watch Recording
                </button>
              )}

              {/* KPI breakdown */}
              {cur.kpi_scores && Object.keys(cur.kpi_scores).length > 0 && (
                <div className="rounded-xl p-4"
                  style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)" }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-3.5 h-3.5 text-yellow-400" />
                    <span className="font-mono-kl text-[10px] uppercase tracking-widest text-white/50">KPI Breakdown</span>
                  </div>
                  {Object.entries(cur.kpi_scores).map(([key, kpi]) => (
                    <KpiBar
                      key={key}
                      label={kpi.label || key}
                      score={kpi.score ?? kpi ?? 0}
                      max={kpi.max || 5}
                      notes={kpi.notes}
                    />
                  ))}
                </div>
              )}

              {/* Strengths */}
              {cur.strengths && cur.strengths.length > 0 && (
                <div className="rounded-xl p-4"
                  style={{ background: "rgba(74,222,128,0.05)", border: "1px solid rgba(74,222,128,0.15)" }}>
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                    <span className="font-mono-kl text-[10px] uppercase tracking-widest text-green-400/70">Strengths</span>
                  </div>
                  <ul className="space-y-2">
                    {cur.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-green-400 text-xs flex-shrink-0 mt-0.5">✓</span>
                        <span className="text-white/60 text-xs leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvement areas */}
              {cur.improvement_areas && cur.improvement_areas.length > 0 && (
                <div className="rounded-xl p-4"
                  style={{ background: "rgba(250,204,21,0.05)", border: "1px solid rgba(250,204,21,0.15)" }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-3.5 h-3.5 text-yellow-400" />
                    <span className="font-mono-kl text-[10px] uppercase tracking-widest text-yellow-400/70">Areas to Improve</span>
                  </div>
                  <div className="space-y-3">
                    {cur.improvement_areas.map((area, i) => (
                      <div key={i}>
                        <div className="text-yellow-400/80 text-xs font-semibold mb-0.5">
                          {area.area || area}
                        </div>
                        {area.suggestion && (
                          <div className="text-white/45 text-xs leading-relaxed">{area.suggestion}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Recording modal */}
      {recording && <RecordingModal sessionId={recording} onClose={() => setRecording(null)} />}
    </>
  );
}

/* ── Main page ── */
export default function Leaderboard() {
  const navigate = useNavigate();
  const [mockTop,     setMockTop]     = useState([]);
  const [blitzTop,    setBlitzTop]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeTab,   setActiveTab]   = useState("mock");
  const [selected,    setSelected]    = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const [m, b] = await Promise.all([
        api.mockLeaderboard().catch(() => ({ top: [] })),
        api.blitzLeaderboard().catch(() => ({ top: [] })),
      ]);
      setMockTop(m.top || []);
      setBlitzTop(b.top || []);
      setLastUpdated(new Date());
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, []);

  return (
    <div className="min-h-screen text-white relative overflow-x-hidden" style={{ background: "var(--kl-navy-deep)" }}>
      <Header />

      <div className="absolute top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(230,57,70,0.6), transparent 65%)" }} />

      <div className="relative z-10 w-full px-6 md:px-12 py-10">

        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-10">
          <button onClick={() => navigate("/")}
            className="font-mono-kl text-xs uppercase tracking-widest text-white/50 hover:text-white flex items-center gap-2 transition">
            ← Home
          </button>
          <div className="flex items-center gap-5">
            {lastUpdated && (
              <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/25">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button onClick={load} className="text-white/40 hover:text-white transition-colors">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Hero */}
        <div className="font-mono-kl text-xs uppercase tracking-[0.35em] text-[color:var(--kl-red)] flex items-center gap-2 mb-3">
          <Trophy className="w-3.5 h-3.5" /> Rankings
        </div>
        <h1 className="font-display text-5xl md:text-8xl uppercase leading-[0.85] tracking-tight mb-3">
          Interview <span className="text-[color:var(--kl-red)]">leaderboard</span>.
        </h1>
        <p className="text-white/40 text-sm md:text-base mb-10 max-w-2xl">
          Top performers from mock interviews — click any candidate to view scores, feedback &amp; recordings.
        </p>

        {/* Tabs */}
        <div className="flex gap-2 mb-10">
          {[
            { id: "mock",  label: "Mock Interviews", count: mockTop.length },
            { id: "blitz", label: "Blitz Quiz",      count: blitzTop.length },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="font-mono-kl text-[10px] uppercase tracking-widest px-4 py-2 rounded-full transition-all"
              style={{
                background: activeTab === tab.id ? "rgba(230,57,70,0.18)" : "rgba(255,255,255,0.04)",
                color:      activeTab === tab.id ? "#e63946" : "rgba(255,255,255,0.45)",
                border:     activeTab === tab.id ? "1px solid rgba(230,57,70,0.45)" : "1px solid rgba(255,255,255,0.08)",
              }}>
              {tab.label}
              <span className="ml-2 opacity-60">{tab.count}</span>
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex items-center gap-3 text-white/40 py-20">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span className="font-mono-kl text-xs uppercase tracking-widest">Loading rankings…</span>
          </div>
        )}

        {/* ── Mock Interview Leaderboard ── */}
        {!loading && activeTab === "mock" && (
          <>
            {mockTop.length >= 3 && (
              <div className="flex flex-col md:flex-row justify-center items-end gap-4 mb-14">
                <PodiumCard entry={mockTop[1]} rank={2} onClick={() => setSelected({ entry: mockTop[1], rank: 2 })} />
                <PodiumCard entry={mockTop[0]} rank={1} hero onClick={() => setSelected({ entry: mockTop[0], rank: 1 })} />
                <PodiumCard entry={mockTop[2]} rank={3} onClick={() => setSelected({ entry: mockTop[2], rank: 3 })} />
              </div>
            )}

            {mockTop.length === 0 ? (
              <div className="rounded-2xl border border-white/8 p-16 text-center" style={{ background: "rgba(255,255,255,0.02)" }}>
                <Trophy className="w-12 h-12 text-white/15 mx-auto mb-4" />
                <div className="font-display text-4xl uppercase text-white/40 mb-2">No Interviews Yet</div>
                <p className="text-white/25 font-mono-kl text-xs uppercase tracking-widest">Completed mock interview scores will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-white/10" style={{ background: "rgba(255,255,255,0.02)" }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/8">
                      <th className="text-left px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">#</th>
                      <th className="text-left px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">Candidate</th>
                      <th className="text-left px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">Best Position</th>
                      <th className="text-left px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35 w-44">Score</th>
                      <th className="text-center px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">Verdict</th>
                      <th className="text-center px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">Sessions</th>
                      <th className="text-right px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">Duration</th>
                      <th className="px-3 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {mockTop.map((s, i) => (
                      <tr key={i} onClick={() => setSelected({ entry: s, rank: i + 1 })}
                        className={`border-b border-white/5 cursor-pointer group transition-colors hover:bg-white/[0.04] ${i < 3 ? "bg-[rgba(230,57,70,0.03)]" : ""}`}>
                        <td className="px-5 py-3"><RankBadge rank={i + 1} /></td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-display text-sm"
                              style={{ background: i < 3 ? "rgba(230,57,70,0.15)" : "rgba(255,255,255,0.06)", color: i < 3 ? "#e63946" : "rgba(255,255,255,0.6)" }}>
                              {(s.name || "?")[0].toUpperCase()}
                            </div>
                            <span className="font-display text-base uppercase text-white group-hover:text-[color:var(--kl-red)] transition-colors">{s.name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 font-mono-kl text-[10px] uppercase tracking-widest text-white/50">{s.position || "—"}</td>
                        <td className="px-5 py-3 w-44"><ScoreBar score={s.total_score} max={s.total_max || 20} /></td>
                        <td className="px-5 py-3 text-center"><VerdictBadge verdict={s.verdict} /></td>
                        <td className="px-5 py-3 text-center">
                          <span className="font-mono-kl text-[10px] px-2 py-0.5 rounded-full"
                            style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)" }}>
                            {(s.sessions || []).length}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="font-mono-kl text-[10px] text-white/40 flex items-center gap-1 justify-end">
                            <Clock className="w-3 h-3" />{fmtDuration(s.duration_seconds)}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/50 transition-colors" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── Blitz Quiz Leaderboard ── */}
        {!loading && activeTab === "blitz" && (
          <>
            {blitzTop.length === 0 ? (
              <div className="rounded-2xl border border-white/8 p-16 text-center" style={{ background: "rgba(255,255,255,0.02)" }}>
                <Trophy className="w-12 h-12 text-white/15 mx-auto mb-4" />
                <div className="font-display text-4xl uppercase text-white/40 mb-2">No Blitz Scores Yet</div>
                <p className="text-white/25 font-mono-kl text-xs uppercase tracking-widest">Completed quiz scores will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-white/10" style={{ background: "rgba(255,255,255,0.02)" }}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/8">
                      <th className="text-left px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">#</th>
                      <th className="text-left px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">Player</th>
                      <th className="text-left px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">Topic</th>
                      <th className="text-right px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">Score</th>
                      <th className="text-center px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">Badge</th>
                      <th className="text-center px-5 py-3 font-mono-kl text-[9px] uppercase tracking-widest text-white/35">Prize 🎁</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blitzTop.map((s, i) => (
                      <tr key={i} className={`border-b border-white/5 ${i < 3 ? "bg-[rgba(230,57,70,0.04)]" : ""}`}>
                        <td className="px-5 py-3"><RankBadge rank={i + 1} /></td>
                        <td className="px-5 py-3 font-display text-base uppercase text-white">{s.name}</td>
                        <td className="px-5 py-3 font-mono-kl text-[10px] uppercase tracking-widest text-white/50">{s.topic_label}</td>
                        <td className="px-5 py-3 text-right">
                          <span className="font-display text-xl text-[color:var(--kl-red)]">{s.certificate_percent}%</span>
                          <span className="font-mono-kl text-[9px] text-white/30 ml-1">({s.score}/10)</span>
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className="font-mono-kl text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                            style={{ background: s.badge === "Expert" ? "rgba(230,57,70,0.2)" : "rgba(255,255,255,0.07)", color: s.badge === "Expert" ? "#e63946" : "rgba(255,255,255,0.55)" }}>
                            {s.badge}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-center">
                          {s.prize_eligible ? <span>🎁</span> : <span className="text-white/15">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <div className="h-20" />
      </div>

      {selected && (
        <CandidateDrawer entry={selected.entry} rank={selected.rank} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

/* ── Podium card ── */
function PodiumCard({ entry, rank, hero, onClick }) {
  const accentClr = { 1: "#e63946", 2: "#60a5fa", 3: "#fb923c" };
  const glows     = {
    1: "0 0 60px rgba(230,57,70,0.35), 0 0 20px rgba(230,57,70,0.2)",
    2: "0 0 30px rgba(96,165,250,0.20)",
    3: "0 0 25px rgba(251,146,60,0.18)",
  };
  const borders   = { 1: "rgba(230,57,70,0.55)", 2: "rgba(96,165,250,0.35)", 3: "rgba(251,146,60,0.30)" };
  const accent    = accentClr[rank];
  const max       = entry.total_max || 20;

  return (
    <div onClick={onClick}
      className="flex flex-col items-center gap-3 cursor-pointer group transition-transform hover:-translate-y-2"
      style={{ minWidth: hero ? 200 : 160 }}>
      {rank === 1 && <Crown className="w-7 h-7 text-[color:var(--kl-red)] drop-shadow-[0_0_14px_rgba(230,57,70,1)]" />}
      <div className="rounded-2xl flex items-center justify-center font-display text-4xl transition-all group-hover:scale-105"
        style={{ width: hero ? 100 : 80, height: hero ? 100 : 80, background: "rgba(255,255,255,0.06)", border: `2px solid ${borders[rank]}`, boxShadow: glows[rank], color: accent }}>
        {(entry.name || "?")[0].toUpperCase()}
      </div>
      <div className="text-center">
        <div className={`font-display uppercase leading-tight ${hero ? "text-2xl" : "text-lg"} text-white group-hover:text-[color:var(--kl-red)] transition-colors`}>
          {entry.name}
        </div>
        <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40 mt-0.5">{entry.position || "—"}</div>
      </div>
      <div className="font-display text-center" style={{ color: accent }}>
        <span className={hero ? "text-4xl" : "text-2xl"}>{entry.total_score}</span>
        <span className="text-white/25 font-mono-kl text-[9px] ml-1">/{max}</span>
      </div>
      <VerdictBadge verdict={entry.verdict} />
      <div className="rounded-t-lg w-full mt-1"
        style={{ height: hero ? 48 : 32, background: `linear-gradient(to top, ${accent}22, ${accent}08)`, border: `1px solid ${borders[rank]}`, borderBottom: "none" }} />
    </div>
  );
}
